import { computed, onMounted, ref } from 'vue';
import useFetch2 from '@/assets/js/composables/fetcher/useFetch.js';
import inputs_to_key_value from '@/assets/js/translators/inputs_to_key_value.translator.js';
import get_response_data from '@/assets/js/helpers/get_response_data';
import { APP_OPTION_PER_PAGE } from '@/assets/js/consts/AppOptions.const';

const useListFetch = (request_handler, options) => {
  options = Object.assign(
    {
      placeholder: {
        list: [],
        total: 0,
      },
      filters: {},
      manual: false,
      caching: {
        enable: false,
        name: null,
        purges: [],
      },
      onFetch: data => {
        return data;
      },
    },
    options
  );

  const EXCLUDED_FILTERS = ['skip', 'take', 'orderby', 'sortby'];

  const is_loadmore_loading = ref(false);

  const filters = ref({
    skip: {
      value: 0,
      default: 0,
    },
    take: {
      label: 'تعداد در صفحه',
      default: APP_OPTION_PER_PAGE,
      value: APP_OPTION_PER_PAGE,
    },
    search: {
      value: '',
      default: '',
    },
    ...options.filters,
  });

  const has_filter = computed(() => {
    return Object.keys(filters.value)
      .filter(key => !EXCLUDED_FILTERS.includes(key))
      .some(key =>
        Array.isArray(filters.value[key].value) ?
          filters.value[key].value.length
        : filters.value[key].value
      );
  });

  const isLoadmoreDone = computed(() => {
    return data.value.total <= filters.value.skip.value + filters.value.take.value;
  });

  const {
    has_error,
    data,
    is_loading,
    fetch: fetch_handler,
  } = useFetch2(request_handler, { ...options, manual: true });

  const fetch = (params = {}) => {
    filters.value.skip.value = 0;

    Object.entries(params).forEach(([key, value]) => {
      if (key in filters.value) filters.value[key].value = value;
      else {
        filters.value[key] = {};
        filters.value[key].value = value;
      }
    });

    return fetch_handler(inputs_to_key_value(filters.value));
  };

  const loadMore = () => {
    filters.value.skip.value += filters.value.take.value;

    is_loadmore_loading.value = true;
    request_handler(inputs_to_key_value(filters.value))
      .then(rs => {
        const rs_data = get_response_data(rs);

        data.value.total = rs_data.total;
        data.value.list = [...data.value.list, ...rs_data.list];
      })
      .finally(() => {
        is_loadmore_loading.value = false;
      })
      .catch(() => {
        has_error.value = true;
      });
  };

  const syncFetch = async () => {
    const currentCount = data.value.list.length;
    const fullListCount = filters.value.skip.value + APP_OPTION_PER_PAGE;

    if (currentCount > fullListCount) {
      const extraCount = currentCount - fullListCount;

      data.value.list.splice(-extraCount, 1);
    } else if (!isLoadmoreDone.value) {
      const params = {
        ...inputs_to_key_value(filters.value),
        skip: currentCount,
        take: fullListCount - currentCount,
      };

      is_loading.value = true;
      try {
        const rs = await request_handler(params);
        const rs_data = get_response_data(rs);

        data.value.total = rs_data.total;
        data.value.list = [...data.value.list, ...rs_data.list];
      } finally {
        is_loading.value = false;
      }
    }
  };

  const createItem = itemData => {
    data.value.list.unshift(itemData);

    data.value.total++;

    syncFetch();
  };

  const updateItem = itemData => {
    const found_index = data.value.list.findIndex(p => p.id === itemData.id);

    if (found_index !== -1) {
      Object.keys(itemData).forEach(key => {
        if (key in data.value.list[found_index]) {
          data.value.list[found_index][key] = itemData[key];
        }
      });
    }
  };

  const removeItem = id => {
    data.value.list = data.value.list.filter(item => item.id !== id);

    data.value.total--;

    syncFetch();
  };

  const paginate = page_num => {
    filters.value.skip.value = (page_num - 1) * filters.value.take.value;

    return fetch_handler(inputs_to_key_value(filters.value));
  };

  const change_per_page = perPage => {
    filters.value.take.value = perPage;
    filters.value.take.default = perPage;

    return fetch_handler(inputs_to_key_value(filters.value));
  };

  const filter = () => {
    filters.value.skip.value = filters.value.skip.default;
    filters.value.take.value = filters.value.take.default;

    return fetch_handler(inputs_to_key_value(filters.value));
  };

  onMounted(() => {
    if (!options.manual) fetch(inputs_to_key_value(filters.value));
  });

  return {
    data,
    has_error,
    is_loading,
    is_loadmore_loading,
    filters,
    has_filter,
    isLoadmoreDone,
    fetch,
    filter,
    loadMore,
    paginate,
    change_per_page,
    syncFetch,
    createItem,
    updateItem,
    removeItem,
  };
};

export default useListFetch;
